import { makeAutoObservable } from 'mobx';
import {
  login,
  register,
  refreshToken,
  getUserMe,
  logout,
  LoginRequest,
  RegisterRequest,
  UserMeResponse,
} from '../api/auth';

class Auth {
  constructor() {
    makeAutoObservable(this);
  }

  token: string | null = null;
  scaToken: string | null = null;
  refreshToken: string | null = null;
  user: UserMeResponse | null = null;
  isAuthenticated: boolean = false;
  isLoading: boolean = false;
  isInitialized: boolean = false;
  isGettingMe: boolean = false;

  // Инициализация токенов из localStorage
  init = async () => {
    if (this.isInitialized) {
      return; // Избегаем повторной инициализации
    }

    const storedToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      this.token = storedToken;
      this.scaToken = storedToken;
      this.isAuthenticated = true;
    }

    if (storedRefreshToken) {
      this.refreshToken = storedRefreshToken;
    }

    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);
      } catch (error) {
        console.error('Failed to parse user from localStorage:', error);
        this.clearAuth();
        this.isInitialized = true;
        return;
      }
    }

    // Если есть токен но нет данных пользователя, получаем их
    if (this.isAuthenticated && !this.user) {
      try {
        await this.getMe();
      } catch (error) {
        console.error('Failed to get user info on init:', error);
        this.clearAuth();
      }
    }

    this.isInitialized = true;
  };

  // Логин
  login = async (credentials: LoginRequest): Promise<void> => {
    this.isLoading = true;
    try {
      const response = await login(credentials);
      this.setTokens(response.access_token, response.refresh_token);
      await this.getMe();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  };

  // Регистрация
  register = async (data: RegisterRequest): Promise<void> => {
    this.isLoading = true;
    try {
      await register(data);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  };

  // Обновление токена
  updateToken = async (): Promise<string> => {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await refreshToken({ refresh_token: this.refreshToken });
      this.setTokens(response.access_token, response.refresh_token);
      return response.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearAuth();
      throw error;
    }
  };

  // Получение информации о пользователе
  getMe = async (): Promise<void> => {
    if (!this.token) {
      throw new Error('No access token available');
    }

    // Избегаем дублирующихся запросов - если уже есть пользователь И не идет загрузка
    if (this.user && !this.isGettingMe) {
      return;
    }

    // Если уже идет запрос, не запускаем новый
    if (this.isGettingMe) {
      return;
    }

    this.isGettingMe = true;
    try {
      const user = await getUserMe(this.token);
      this.user = user;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('Failed to get user info:', error);
      // Не очищаем auth здесь, чтобы избежать циклов
      // Пусть вызывающая сторона решает что делать с ошибкой
      throw error;
    } finally {
      this.isGettingMe = false;
    }
  };

  // Выход
  logout = async (): Promise<void> => {
    if (this.refreshToken) {
      try {
        await logout({ refresh_token: this.refreshToken });
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }
    this.clearAuth();
  };

  // Установка токенов
  setTokens = (accessToken: string, refreshToken: string): void => {
    this.token = accessToken;
    this.scaToken = accessToken;
    this.refreshToken = refreshToken;
    this.isAuthenticated = true;

    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  };

  // Очистка авторизации
  clearAuth = (): void => {
    this.token = null;
    this.scaToken = null;
    this.refreshToken = null;
    this.user = null;
    this.isAuthenticated = false;
    this.isGettingMe = false;

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    // Очищаем userStore при логауте
    // Импортируем динамически, чтобы избежать циклических зависимостей
    import('./User').then((module) => {
      module.default.reset();
    });
  };

  // Проверка авторизации (для старой системы совместимости)
  authorize = (): void => {
    if (!this.isAuthenticated) {
      // Перенаправляем на страницу логина
      window.location.href = '/login';
    }
  };
}

const auth = new Auth();
export default auth;
